import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { getDebateTarget as resolveDebateTarget, isDebatePast, LIVE_WINDOW } from '../utils/debateTime';
import { CalendarCheck, TrendingUp, ArrowRight, Award } from 'lucide-react';
import { fetchPopularPetitions } from '../services/petitionService';
import { slugify } from '../utils/urlUtils';

// ── Countdown helpers ──────────────────────────────────────────────────────────
const padN = n => String(n).padStart(2, '0');

function getDebateTarget(dateStr) {
  const t = resolveDebateTarget(dateStr);
  return t ? t.getTime() : null;
}

function formatDebateDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const MiniCountdown = ({ dateStr }) => {
  const [diff, setDiff] = useState(() => {
    const t = getDebateTarget(dateStr);
    return t ? t - Date.now() : null;
  });

  useEffect(() => {
    const target = getDebateTarget(dateStr);
    if (!target) return;
    const id = setInterval(() => setDiff(target - Date.now()), 1000);
    return () => clearInterval(id);
  }, [dateStr]);

  if (!diff) return null;

  // Show LIVE badge during the 3-hour debate window
  if (diff <= 0 && diff > -LIVE_WINDOW) {
    return (
      <LiveBadge>
        <span className="live-dot" />
        Debate Live Now
      </LiveBadge>
    );
  }
  if (diff <= -LIVE_WINDOW) return null;

  const isUrgent = diff < 2 * 86400 * 1000;
  const totalSecs = Math.floor(diff / 1000);
  const days  = Math.floor(totalSecs / 86400);
  const hrs   = Math.floor((totalSecs % 86400) / 3600);
  const mins  = Math.floor((totalSecs % 3600) / 60);
  const secs  = totalSecs % 60;

  const units = [
    { val: days, max: 30, label: 'Days' },
    { val: hrs,  max: 24, label: 'Hrs'  },
    { val: mins, max: 60, label: 'Min'  },
    { val: secs, max: 60, label: 'Sec'  },
  ];

  const SIZE  = 36;
  const STROKE = 3;
  const R     = (SIZE - STROKE) / 2;
  const CIRC  = 2 * Math.PI * R;

  return (
    <CountdownCircles>
      <span className="cd-intro">Debate starts in</span>
      {units.map((u, i) => {
        const pct    = u.max > 0 ? u.val / u.max : 0;
        const offset = CIRC * (1 - pct);
        return (
          <React.Fragment key={u.label}>
            <div className="cd-unit">
              <div className="cd-ring-wrap">
                <svg width={SIZE} height={SIZE} style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx={SIZE/2} cy={SIZE/2} r={R} className="cd-track" />
                  <circle cx={SIZE/2} cy={SIZE/2} r={R}
                    className={`cd-arc${isUrgent ? ' urgent' : ''}`}
                    strokeDasharray={CIRC}
                    strokeDashoffset={offset}
                  />
                </svg>
                <span className="cd-num">{padN(u.val)}</span>
              </div>
              <span className="cd-name">{u.label}</span>
            </div>
            {i < units.length - 1 && <span className="cd-sep">:</span>}
          </React.Fragment>
        );
      })}
    </CountdownCircles>
  );
};

// ── Live badge ─────────────────────────────────────────────────────────────────
const livePulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`;

const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
  font-size: 0.72em;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-top: 6px;
  .live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #f87171;
    animation: ${livePulse} 1s ease-in-out infinite;
    flex-shrink: 0;
  }
`;

// ── Styled components ──────────────────────────────────────────────────────────
const Section = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1024px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 640px)  { grid-template-columns: 1fr; }
`;

const ColCard = styled.div`
  background: rgba(255,255,255,0.97);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.4);
  display: flex;
  flex-direction: column;
`;

const ColCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: ${p => p.variant === 'debate'
    ? 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 60%, #4f46e5 100%)'
    : p.variant === 'trending'
    ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
    : 'linear-gradient(135deg, #92400e 0%, #b45309 60%, #d97706 100%)'};

  svg { color: rgba(255,255,255,0.9); flex-shrink: 0; }

  h3 {
    color: white;
    font-size: 1em;
    font-weight: 700;
    margin: 0;
  }

  .col-sub {
    color: rgba(255,255,255,0.75);
    font-size: 0.75em;
    margin-top: 1px;
  }

  .col-badge {
    margin-left: auto;
    background: rgba(255,255,255,0.2);
    color: white;
    font-size: 0.72em;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

const ColCardBody = styled.div`
  padding: 4px 10px 10px;
  flex: 1;
`;

const MiniPetitionItem = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid #cbd5e0;
  padding: 10px 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s ease;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  position: relative;

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${p => p.variant === 'debate'
      ? 'rgba(79,70,229,0.05)'
      : p.variant === 'trending'
      ? 'rgba(5,150,105,0.05)'
      : 'rgba(217,119,6,0.05)'};
  }

  .item-rank {
    min-width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
    color: white;
    font-size: 0.72em;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
    box-shadow: 0 2px 6px rgba(79,70,229,0.35);
  }

  .item-body { flex: 1; min-width: 0; }

  .item-title {
    color: #1a202c;
    font-size: 1.15em;
    font-weight: 700;
    line-height: 1.45;
    margin-bottom: 8px;
    cursor: pointer;
    @media (max-width: 768px) {
      font-size: 0.85em;
    }
  }

  .item-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }

  .item-sigs {
    font-size: 1.02em;
    font-weight: 800;
    color: ${p => p.variant === 'debate' ? '#4f46e5'
      : p.variant === 'trending' ? '#059669'
      : '#d97706'};
  }

  .item-date {
    font-size: 0.92em;
    color: #718096;
  }

  .item-responded {
    font-size: 0.75em;
    background: #d1fae5;
    color: #065f46;
    padding: 2px 7px;
    border-radius: 10px;
    font-weight: 600;
  }

  .item-arrow {
    color: #cbd5e0;
    flex-shrink: 0;
    margin-top: 4px;
    transition: color 0.18s, transform 0.18s;
    cursor: pointer;
  }

  &:hover .item-arrow {
    color: #667eea;
    transform: translateX(3px);
  }
`;

const ColEmptyState = styled.div`
  color: #a0aec0;
  font-size: 0.95em;
  text-align: center;
  padding: 36px 20px;
  line-height: 1.7;
`;

const ColLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  gap: 14px;

  .spinner-ring {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 3px solid #e2e8f0;
    border-top-color: ${p =>
      p.variant === 'debate'  ? '#4f46e5' :
      p.variant === 'trending'? '#059669' : '#d97706'};
    animation: colSpin 0.7s linear infinite;
  }

  .spinner-text {
    font-size: 0.82em;
    color: #a0aec0;
    font-weight: 500;
  }

  @keyframes colSpin { to { transform: rotate(360deg); } }
`;

const ColViewAllButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 11px;
  border: none;
  border-top: 1px solid #cbd5e0;
  background: #f7fafc;
  color: #4a5568;
  font-size: 0.85em;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover { background: #edf2f7; color: #2d3748; }
  &:hover svg { transform: translateX(3px); }
  svg { transition: transform 0.15s; }
`;


const CountdownCircles = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 7px;

  .cd-intro { font-size: 0.7em; color: #718096; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; margin-right: 3px; white-space: nowrap; }
  .cd-unit  { display: flex; flex-direction: column; align-items: center; gap: 2px; }
  .cd-ring-wrap { position: relative; display: flex; align-items: center; justify-content: center; }
  .cd-track { fill: none; stroke: rgba(203,213,224,0.6); stroke-width: 3; }
  .cd-arc   { fill: none; stroke: #4f46e5; stroke-width: 3; stroke-linecap: round; transition: stroke-dashoffset 0.6s ease; }
  .cd-arc.urgent { stroke: #ef4444; }
  .cd-num   { position: absolute; font-size: 0.74em; font-weight: 800; color: #2d3748; font-variant-numeric: tabular-nums; letter-spacing: -0.5px; }
  .cd-name  { font-size: 0.58em; color: #a0aec0; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; }
  .cd-sep   { font-size: 0.9em; font-weight: 800; color: #cbd5e0; margin-bottom: 12px; line-height: 1; }
`;

// ── Main component ─────────────────────────────────────────────────────────────
const PetitionHighlights = () => {
  const navigate = useNavigate();
  const [upcomingDebates, setUpcomingDebates] = useState([]);
  const [trendingNow,     setTrendingNow]     = useState([]);
  const [openPetitions,   setOpenPetitions]   = useState([]);
  const [loadingPetitions, setLoadingPetitions] = useState(true);
  const [loadingTrending,  setLoadingTrending]  = useState(true);

  const loadPetitions = useCallback(async () => {
    try {
      const openData = await fetchPopularPetitions(null, 'open');
      const now = new Date();
          const debateData = (openData || [])
        .filter(p => p.scheduledDebateDate && !isDebatePast(p.scheduledDebateDate, now))
        .sort((a, b) => new Date(a.scheduledDebateDate) - new Date(b.scheduledDebateDate));
      setOpenPetitions(openData || []);
      setUpcomingDebates(debateData.slice(0, 5));
    } catch {
      setOpenPetitions([]);
    } finally {
      setLoadingPetitions(false);
    }
  }, []);

  useEffect(() => { loadPetitions(); }, [loadPetitions]);

  useEffect(() => {
    const load = async () => {
      const urls = process.env.NODE_ENV === 'production'
        ? ['https://timedata.ukpetitiontracker.com']
        : ['http://localhost:5001', 'https://timedata.ukpetitiontracker.com'];
      for (const base of urls) {
        try {
          const res = await fetch(`${base}/trending?hours=2`);
          if (!res.ok) continue;
          const data = await res.json();
          const petitions = data.trending_petitions || [];
          if (petitions.length > 0) {
            setTrendingNow(petitions.slice(0, 7));
            setLoadingTrending(false);
            return;
          }
        } catch { }
      }
      setTrendingNow([]);
      setLoadingTrending(false);
    };
    load();
  }, []);

  return (
    <>
      <Section>
        {/* Upcoming Debates */}
        <ColCard>
          <ColCardHeader variant="debate">
            <CalendarCheck />
            <div>
              <h3>Upcoming Debates</h3>
              <div className="col-sub">Scheduled in Parliament</div>
            </div>
            {upcomingDebates.length > 0 && (
              <span className="col-badge">{upcomingDebates.length} scheduled</span>
            )}
          </ColCardHeader>
          <ColCardBody>
            {loadingPetitions ? (
              <ColLoader variant="debate"><div className="spinner-ring" /><span className="spinner-text">Loading debates...</span></ColLoader>
            ) : upcomingDebates.length === 0 ? (
              <ColEmptyState>No debates currently scheduled.<br />Check back soon.</ColEmptyState>
            ) : (
              upcomingDebates.map(p => (
                <MiniPetitionItem key={p.id} variant="debate"
                  onClick={() => navigate(`/petition/${p.id}/${slugify(p.title)}`)}
                >
                  <div className="item-body">
                    <div className="item-title">{p.title}</div>
                    <div className="item-meta">
                      <span className="item-sigs">{(p.signatureCount || 0).toLocaleString()} signatures</span>
                      <span className="item-date">{formatDebateDate(p.scheduledDebateDate)}</span>
                    </div>
                    <MiniCountdown dateStr={p.scheduledDebateDate} />
                  </div>
                </MiniPetitionItem>
              ))
            )}
          </ColCardBody>
          <ColViewAllButton type="button" onClick={() => navigate('/petitions/debate-scheduled')}>
            View All Debates <ArrowRight size={14} />
          </ColViewAllButton>
        </ColCard>

        {/* Trending Now */}
        <ColCard>
          <ColCardHeader variant="trending">
            <TrendingUp />
            <div>
              <h3>Trending Now</h3>
              <div className="col-sub">Fastest growing last 2 hours</div>
            </div>
            <span className="col-badge">Live</span>
          </ColCardHeader>
          <ColCardBody>
            {loadingTrending ? (
              <ColLoader variant="trending"><div className="spinner-ring" /><span className="spinner-text">Loading trends...</span></ColLoader>
            ) : trendingNow.length === 0 ? (
              <ColEmptyState>No trending data available.</ColEmptyState>
            ) : (
              trendingNow.map((p, i) => (
                <MiniPetitionItem key={p.petition_id} variant="trending"
                  onClick={() => navigate(`/petition/${p.petition_id}/${slugify(p.title)}`)}
                >
                  <span className="item-rank">{i + 1}</span>
                  <div className="item-body">
                    <div className="item-title">{p.title}</div>
                    <div className="item-meta">
                      <span className="item-sigs">{(p.current_signatures || 0).toLocaleString()} signatures</span>
                      <span className="item-date">+{(p.signature_growth || 0).toLocaleString()} in last 2 hours</span>
                    </div>
                  </div>
                </MiniPetitionItem>
              ))
            )}
          </ColCardBody>
          <ColViewAllButton type="button" onClick={() => navigate('/petitions/trending')}>
            View All Trending <ArrowRight size={14} />
          </ColViewAllButton>
        </ColCard>

        {/* Popular Petitions */}
        <ColCard>
          <ColCardHeader variant="popular">
            <Award />
            <div>
              <h3>Popular Petitions</h3>
              <div className="col-sub">Open · Most signatures</div>
            </div>
            <span className="col-badge">Top 7</span>
          </ColCardHeader>
          <ColCardBody>
            {loadingPetitions ? (
              <ColLoader variant="popular"><div className="spinner-ring" /><span className="spinner-text">Loading petitions...</span></ColLoader>
            ) : openPetitions.length === 0 ? (
              <ColEmptyState>No data available.</ColEmptyState>
            ) : (
              openPetitions.slice(0, 7).map((p, i) => (
                <MiniPetitionItem key={p.id} variant="popular"
                  onClick={() => navigate(`/petition/${p.id}/${slugify(p.title)}`)}
                >
                  <span className="item-rank" style={{ background: 'linear-gradient(135deg, #92400e 0%, #d97706 100%)', boxShadow: '0 2px 6px rgba(217,119,6,0.35)' }}>{i + 1}</span>
                  <div className="item-body">
                    <div className="item-title">{p.title}</div>
                    <div className="item-meta">
                      <span className="item-sigs">{(p.signatureCount || 0).toLocaleString()} signatures</span>
                      {p.hasGovernmentResponse && <span className="item-responded">Responded</span>}
                    </div>
                  </div>
                </MiniPetitionItem>
              ))
            )}
          </ColCardBody>
          <ColViewAllButton type="button" onClick={() => navigate('/petitions/open')}>
            View All Open Petitions <ArrowRight size={14} />
          </ColViewAllButton>
        </ColCard>
      </Section>

    </>
  );
};

export default PetitionHighlights;
